import { useEffect, useMemo, useState } from 'react'
import { SearchIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Spacer,
  Tooltip
} from '@chakra-ui/react'
import { observer } from 'mobx-react'
import { useLocation, useNavigate } from 'react-router-dom'
import SvgIcon from 'components/SvgIcon'
import { EEsGlobalSearchCollection } from 'constants/elasticSearch'
import { useStores } from 'hooks/useStores'
import { useActivePlayProfile } from 'hooks/use-active-play-profile'
import { getAddableProfileTypes } from 'utils/profile-eligibility'
import { MOBILE_BANNER_HEIGHT_VAR, MOBILE_MAX_WIDTH } from 'constants/mobile-page-template'
import PlayDashboardBell from 'pages/PlayDashboardPage/components/play-dashboard-bell'
import ProfileSwitcher from 'pages/PlayDashboardPage/components/ProfileSwitcher'
import PlayInboxButton from './components/play-inbox-button'
import routes from 'routes'
import { AuthRoleIdEnum } from 'constants/user'
import {
  PLAY_DASHBOARD_BODY_BG,
  PLAY_TOPBAR_BUTTON_BG,
  PLAY_TOPBAR_BUTTON_BORDER,
  PLAY_TOPBAR_HEIGHT,
  PLAY_TOPBAR_INPUT_BG,
  PLAY_TOPBAR_INPUT_BORDER
} from 'pages/PlayDashboardPage/mock-data'

interface IPlayTopbarProps {
  toggleSidebarCollapse: () => void
  setShowSidebarMobile: () => void
  showSidebarMobile: boolean
  // Current sidebar width on md+. Used to offset the fixed topbar so it
  // doesn't sit behind the sidebar. Mobile branch ignores this.
  isCondensed?: boolean
}

// Must mirror `$leftbar-width` in styles/themes/default/_custom-variables.scss
// (and the inline width in LeftSidebar). Update all three together so the topbar
// keeps its left offset aligned with the sidebar edge.
const SIDEBAR_WIDTH_EXPANDED = '220px'
const SIDEBAR_WIDTH_CONDENSED = '70px'

// PlayTopbar — dark-mode 48px topbar built to Figma spec (images #18/#19/#20).
// Cross-team migration: mounted via VerticalLayout's `usePlayTopbar` prop for
// every authed route. The notification bell and profile chip reuse the same
// components MobileShell renders inline, so the switcher dropdown + notification
// list stay in sync across viewports.
const PlayTopbar = ({ toggleSidebarCollapse, setShowSidebarMobile, isCondensed }: IPlayTopbarProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { searchStore, profileStore, authStore } = useStores()
  const { profiles, active, setActive, raw } = useActivePlayProfile()
  const [keyword, setKeyword] = useState<string>('')

  // The keyword box mirrors the URL: /search is both the results list and My Stacks,
  // so leaving a stale keyword in the box after landing on the keyword-less My Stacks
  // would describe a search the page is no longer showing.
  useEffect(() => {
    const searchTextParam = new URLSearchParams(location.search).get('searchText') ?? ''
    setKeyword(location.pathname === routes.search.value ? searchTextParam : '')
  }, [location.pathname, location.search])

  // Stacks are open to players again, so global search / "My Stacks" is no longer
  // coach-gated: every play user gets the field.
  // The phone has no room for the field beside the icon group, so the magnifier
  // opens it underneath the bar instead of jumping straight to the results page.
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState<boolean>(false)
  // The header shortcut is the Content Item library. It is a staff surface, so on
  // the player side — a Player, or staff on a player/youth profile — it stays in
  // place but disabled.
  const canUseLibrary = authStore.hasRoleAtLeast(AuthRoleIdEnum.COACH) && (!active || active.role === 'coach')

  const addableTypes = useMemo(() => getAddableProfileTypes(raw), [raw])

  const handleAddSuccess = () => {
    if (window.location.pathname === routes.playDashboard.value) {
      profileStore.loadMyProfiles()
    } else {
      window.location.assign(routes.playDashboard.value)
    }
  }

  const handleSidebarClick = (): void => {
    if (window.innerWidth <= MOBILE_MAX_WIDTH) {
      setShowSidebarMobile()
      return
    }
    toggleSidebarCollapse()
  }

  const handleSearchSubmit = (): void => {
    // Reuse the legacy global-search flow: reset filter state, route to
    // /search with the keyword query. Mirrors the old Topbar `gotoSearchPage`
    // behavior so the new chrome surfaces the same search experience.
    const text = keyword.trim()
    searchStore.clearSearchResults()
    searchStore.setManageMode(false)
    searchStore.clearSearchFilter()
    searchStore.setDisplayTypes([EEsGlobalSearchCollection.PROCESS])
    searchStore.setSearchText(text)
    searchStore.changeSearchFilter({
      collections: [],
      groups: [],
      sort: ''
    })
    searchStore.search()
    setIsMobileSearchOpen(false)
    navigate(`${routes.search.value}?searchText=${encodeURIComponent(text)}&page=1`)
  }

  return (
    <>
      <Flex
        as="nav"
        h={PLAY_TOPBAR_HEIGHT}
        bg={PLAY_DASHBOARD_BODY_BG}
        align="center"
        px={4}
        position="fixed"
        // Steps down by the "Better on desktop" advisory while it is on screen; the
        // property is absent otherwise, so the fallback pins the bar to the top.
        top={{ base: `var(${MOBILE_BANNER_HEIGHT_VAR}, 0px)`, md: 0 }}
        left={{ base: 0, md: isCondensed ? SIDEBAR_WIDTH_CONDENSED : SIDEBAR_WIDTH_EXPANDED }}
        right={0}
        zIndex={1001}
        transition="left 0.2s ease-out"
      >
        {/* Left: sidebar toggle pinned to the far-left edge. Hidden on mobile —
          mobile users navigate via the BottomNav footer instead of a side panel. */}
        <Box
          as="button"
          aria-label="Toggle sidebar"
          onClick={handleSidebarClick}
          display={{ base: 'none', md: 'inline-flex' }}
          alignItems="center"
          justifyContent="center"
          background="transparent"
          border="none"
          p={0}
          cursor="pointer"
        >
          <SvgIcon iconName="octicon_sidebar-collapse-16" size={20} />
        </Box>

        {/* Mobile-only left icons: search + My Stacks (20×20). On md+ the sidebar
          toggle + full search bar take over instead. */}
        <HStack spacing={4} display={{ base: 'flex', md: 'none' }}>
          {/* Search — shown for all play users per design (was coach-gated). */}
          <Box
            as="button"
            type="button"
            aria-label="Search"
            onClick={() => setIsMobileSearchOpen(isOpen => !isOpen)}
            border="none"
            bg="transparent"
            p={0}
            cursor="pointer"
            display="inline-flex"
            alignItems="center"
            _focus={{ boxShadow: 'none', outline: 'none' }}
            _focusVisible={{ boxShadow: 'none', outline: 'none' }}
            sx={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <SvgIcon iconName="search" size={20} color="#FFFFFF" />
          </Box>
          {/* Content Items shortcut — same route as the desktop icon beside the search bar. */}
          <Box
            as="button"
            type="button"
            aria-label="Content Items"
            disabled={!canUseLibrary}
            onClick={() => navigate(routes.contentItems.value)}
            border="none"
            bg="transparent"
            p={0}
            cursor={canUseLibrary ? 'pointer' : 'not-allowed'}
            opacity={canUseLibrary ? 1 : 0.4}
            display="inline-flex"
            alignItems="center"
            _focus={{ boxShadow: 'none', outline: 'none' }}
            _focusVisible={{ boxShadow: 'none', outline: 'none' }}
            sx={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <SvgIcon iconName="draft-icon" size={20} color="#FFFFFF" />
          </Box>
        </HStack>

        <Spacer />

        {/* Middle: search + filter, capped at 640px so it doesn't crowd the right group.
          Hidden on mobile — the cramped 320px viewport can't host the search bar
          and the icon group together; mobile users get just the toggle + right
          icon group. */}
        <HStack w="640px" maxW="50%" gap="8px" display={{ base: 'none', md: 'flex' }}>
          <InputGroup size="sm" h="32px">
            <Input
              type="search"
              placeholder="Enter keywords..."
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearchSubmit()}
              h="32px"
              pr="92px"
              bg={PLAY_TOPBAR_INPUT_BG}
              border={`1px solid ${PLAY_TOPBAR_INPUT_BORDER}`}
              borderRadius="8px"
              color="white"
              fontSize="13px"
              _placeholder={{ color: 'rgba(255,255,255,0.45)' }}
              _hover={{ borderColor: 'rgba(194,199,214,0.24)' }}
              _focus={{ borderColor: 'rgba(194,199,214,0.32)', boxShadow: 'none' }}
            />
            <InputRightElement w="84px" h="32px" pr={1}>
              <Button
                size="sm"
                h="24px"
                w="76px"
                borderRadius="6px"
                bg={PLAY_TOPBAR_BUTTON_BG}
                color="white"
                fontSize="12px"
                fontWeight={500}
                gap="6px"
                border={`1px solid ${PLAY_TOPBAR_BUTTON_BORDER}`}
                _hover={{ bg: 'rgba(255,255,255,0.12)' }}
                onClick={handleSearchSubmit}
              >
                Search
                <SearchIcon boxSize="12px" />
              </Button>
            </InputRightElement>
          </InputGroup>

          {/* Content Items shortcut beside the search; disabled on the player side.
              Hover shows the tooltip. */}
          <Tooltip label="Content Items">
            <IconButton
              aria-label="Content Items"
              isDisabled={!canUseLibrary}
              icon={<SvgIcon iconName="process-icon" size={16} color="#FFFFFF" />}
              onClick={() => navigate(routes.contentItems.value)}
              size="sm"
              w="32px"
              h="32px"
              minW="32px"
              borderRadius="8px"
              border={`1px solid ${PLAY_TOPBAR_BUTTON_BORDER}`}
              bg={PLAY_TOPBAR_BUTTON_BG}
              color="white"
              _hover={{ bg: 'rgba(255,255,255,0.12)' }}
            />
          </Tooltip>
        </HStack>

        <Spacer />

        {/* Right: messages, notifications, team switch, athlete/coach profile.
          Every icon is a dark-themed component built to match the new chrome —
          legacy Topbar functionality (inbox count, real notifications list,
          team switch, logout + account settings) merged in via the new
          components instead of mounting the legacy light-themed widgets. */}
        <HStack gap="12px">
          <PlayInboxButton />
          <PlayDashboardBell />
          {/* No `expanded` → trigger collapses to just the avatar circle on mobile
            (label/role/chevron only appear on lg+). */}
          <ProfileSwitcher
            profiles={profiles}
            active={active}
            onChange={setActive}
            addableTypes={addableTypes}
            onAddProfileSuccess={handleAddSuccess}
          />
        </HStack>
      </Flex>
      {isMobileSearchOpen && (
        <Box
          display={{ base: 'block', md: 'none' }}
          position="fixed"
          top={`calc(var(${MOBILE_BANNER_HEIGHT_VAR}, 0px) + ${PLAY_TOPBAR_HEIGHT})`}
          left={0}
          right={0}
          zIndex={1001}
          bg={PLAY_DASHBOARD_BODY_BG}
          px={4}
          pb={3}
        >
          <InputGroup size="sm" h="32px">
            <Input
              autoFocus
              type="search"
              placeholder="Enter keywords..."
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearchSubmit()}
              h="32px"
              pr="92px"
              bg={PLAY_TOPBAR_INPUT_BG}
              border={`1px solid ${PLAY_TOPBAR_INPUT_BORDER}`}
              borderRadius="8px"
              color="white"
              fontSize="13px"
              _placeholder={{ color: 'rgba(255,255,255,0.45)' }}
              _hover={{ borderColor: 'rgba(194,199,214,0.24)' }}
              _focus={{ borderColor: 'rgba(194,199,214,0.32)', boxShadow: 'none' }}
            />
            <InputRightElement w="84px" h="32px" pr={1}>
              <Button
                size="sm"
                h="24px"
                w="76px"
                borderRadius="6px"
                bg={PLAY_TOPBAR_BUTTON_BG}
                color="white"
                fontSize="12px"
                fontWeight={500}
                gap="6px"
                border={`1px solid ${PLAY_TOPBAR_BUTTON_BORDER}`}
                _hover={{ bg: 'rgba(255,255,255,0.12)' }}
                onClick={handleSearchSubmit}
              >
                Search
                <SearchIcon boxSize="12px" />
              </Button>
            </InputRightElement>
          </InputGroup>
        </Box>
      )}
    </>
  )
}

export default observer(PlayTopbar)
